// ./01 - PROJETOS DEV/meu-controle-de-estoque/src/components/modals/AddPurchaseModal.jsx
import React, { useState, useEffect } from 'react';

const AddPurchaseModal = ({ isOpen, onClose, onSave }) => {
    
    const getNextMonthDate = (baseDate = new Date()) => {
        const date = new Date(baseDate);
        date.setMonth(date.getMonth() + 1);
        return date.toISOString().split('T')[0];
    };

    const initialState = {
        description: '',
        totalAmount: '',
        purchaseDate: new Date().toISOString().split('T')[0],
        paymentType: 'À Vista',
        paymentMethod: 'Pix',
        installmentCount: 2,
        firstDueDate: new Date().toISOString().split('T')[0],
        termDate: getNextMonthDate()
    };

    const [formState, setFormState] = useState(initialState);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setFormState(initialState);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        setFormState(prev => {
            const newState = { ...prev, [name]: value };
            
            if (name === 'purchaseDate') {
                newState.firstDueDate = value; 
                
                // BLINDAGEM: Só tenta calcular o próximo mês se a data estiver completa (10 caracteres ex: 2026-05-11)
                if (value && value.length === 10) {
                    const pd = new Date(`${value}T12:00:00`);
                    if (!isNaN(pd.getTime())) { 
                        newState.termDate = getNextMonthDate(pd);
                    }
                }
            }
            
            return newState;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (isSaving) return;
        setIsSaving(true);

        const totalValue = parseFloat(formState.totalAmount);
        if (isNaN(totalValue) || totalValue <= 0) {
            alert("Por favor, insira um valor total válido.");
            setIsSaving(false);
            return;
        }

        // BLINDAGEM NO SUBMIT: Checagem rigorosa de datas válidas antes de salvar
        const parsedPurchaseDate = new Date(`${formState.purchaseDate}T12:00:00`);
        if (isNaN(parsedPurchaseDate.getTime())) {
            alert("A Data da Compra está inválida ou incompleta.");
            setIsSaving(false);
            return;
        }

        const parsedFirstDueDate = new Date(`${formState.firstDueDate}T12:00:00`);
        if (isNaN(parsedFirstDueDate.getTime())) {
            alert("A Data do Pagamento/Parcela está inválida ou incompleta.");
            setIsSaving(false);
            return;
        }

        const purchaseData = {
            description: formState.description,
            totalAmount: totalValue,
            purchaseDate: parsedPurchaseDate, // Data perfeitamente validada
            paymentType: formState.paymentType,
            paymentMethod: formState.paymentMethod,
            status: 'Pendente',
            installments: [],
            payments: []
        };

        if (purchaseData.paymentType === 'À Vista') {
            purchaseData.status = 'Concluída';
            
            purchaseData.installments.push({ 
                installmentNumber: 1, 
                amount: totalValue, 
                dueDate: parsedFirstDueDate, 
                status: 'Paga', 
                paidAmount: totalValue 
            });
            
            purchaseData.payments.push({
                id: crypto.randomUUID(),
                amount: totalValue,
                date: parsedFirstDueDate,
                method: purchaseData.paymentMethod
            });

        } else if (purchaseData.paymentType === 'Por Prazo') {
            const parsedTermDate = new Date(`${formState.termDate}T12:00:00`);
            if (isNaN(parsedTermDate.getTime())) {
                alert("A Data de Vencimento está inválida.");
                setIsSaving(false);
                return;
            }
            purchaseData.installments.push({ 
                installmentNumber: 1, 
                amount: totalValue, 
                dueDate: parsedTermDate, 
                status: 'Pendente', 
                paidAmount: 0 
            });

        } else if (purchaseData.paymentType === 'Parcelado') {
            const numInstallments = Number(formState.installmentCount);
            const installmentAmount = parseFloat((totalValue / numInstallments).toFixed(2));
            
            const generatedInstallments = Array.from({ length: numInstallments }, (_, i) => {
                const dueDate = new Date(parsedFirstDueDate);
                dueDate.setMonth(dueDate.getMonth() + i);
                return { 
                    installmentNumber: i + 1, 
                    amount: installmentAmount, 
                    dueDate: dueDate, 
                    status: 'Pendente', 
                    paidAmount: 0 
                };
            });
            
            const totalCalculated = generatedInstallments.reduce((acc, inst) => acc + inst.amount, 0);
            const diff = totalValue - totalCalculated;
            if (generatedInstallments.length > 0) {
                generatedInstallments[generatedInstallments.length - 1].amount += parseFloat(diff.toFixed(2));
            }
            purchaseData.installments = generatedInstallments;
        }

        try {
            await onSave(purchaseData);
            onClose();
        } catch (error) {
            console.error("Falha ao registrar compra:", error);
            alert(`Erro ao salvar: ${error.message}`);
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay z-50 flex items-center justify-center fixed inset-0 bg-black bg-opacity-50">
            <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 w-full max-w-2xl m-4 sm:m-8 max-h-[90vh] overflow-y-auto">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Registrar Compromisso Financeiro</h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                            <label htmlFor="description" className="form-label">Descrição da Compra</label>
                            <input id="description" name="description" value={formState.description} onChange={handleChange} type="text" placeholder="Ex: Lote de Maquiagem, Fornecedor X" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-[#D7BFBD] focus:border-[#D7BFBD]" required />
                        </div>
                        
                        <div>
                            <label htmlFor="totalAmount" className="form-label">Valor Total (R$)</label>
                            <input id="totalAmount" name="totalAmount" value={formState.totalAmount} onChange={handleChange} type="number" step="0.01" placeholder="0,00" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-[#D7BFBD] focus:border-[#D7BFBD]" required />
                        </div>

                        <div>
                            <label htmlFor="purchaseDate" className="form-label">Data da Compra</label>
                            <input id="purchaseDate" name="purchaseDate" type="date" value={formState.purchaseDate} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-[#D7BFBD] focus:border-[#D7BFBD]" required />
                        </div>
                    </div>

                    <div className="space-y-4 pt-4 border-t border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-800">Condições de Pagamento</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="form-label">Tipo de Pagamento</label>
                                <select name="paymentType" value={formState.paymentType} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-[#D7BFBD] focus:border-[#D7BFBD]">
                                    <option value="À Vista">À Vista</option>
                                    <option value="Por Prazo">Por Prazo</option>
                                    <option value="Parcelado">Parcelado</option>
                                </select>
                            </div>
                            <div>
                                <label className="form-label">Método de Pagamento</label>
                                <select name="paymentMethod" value={formState.paymentMethod} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-[#D7BFBD] focus:border-[#D7BFBD]">
                                    <option value="Pix">Pix</option>
                                    <option value="Cartão de Crédito">Cartão de Crédito</option>
                                    <option value="Boleto">Boleto</option>
                                    <option value="Dinheiro">Dinheiro</option>
                                </select>
                            </div>
                        </div>

                        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-4">
                            {formState.paymentType === 'À Vista' && (
                                <div>
                                    <label className="form-label">Data do Desembolso</label>
                                    <input name="firstDueDate" type="date" value={formState.firstDueDate} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-[#D7BFBD] focus:border-[#D7BFBD]" required />
                                </div>
                            )}

                            {formState.paymentType === 'Por Prazo' && (
                                <div>
                                    <label className="form-label">Data de Vencimento</label>
                                    <input name="termDate" type="date" value={formState.termDate} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-[#D7BFBD] focus:border-[#D7BFBD]" required />
                                </div>
                            )}

                            {formState.paymentType === 'Parcelado' && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="form-label">Data da 1ª Parcela</label>
                                        <input name="firstDueDate" type="date" value={formState.firstDueDate} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-[#D7BFBD] focus:border-[#D7BFBD]" required />
                                    </div>
                                    <div>
                                        <label className="form-label">Quantidade de Parcelas</label>
                                        <select name="installmentCount" value={formState.installmentCount} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-[#D7BFBD] focus:border-[#D7BFBD]">
                                            {Array.from({ length: 11 }, (_, i) => i + 2).map(n => (
                                                <option key={n} value={n}>{n}x</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex flex-col-reverse sm:flex-row justify-end gap-4 pt-6 border-t border-gray-200 mt-6">
                        <button type="button" onClick={onClose} className="py-2 px-6 bg-gray-200 text-gray-800 font-semibold rounded-lg hover:bg-gray-300 w-full sm:w-auto">Cancelar</button>
                        <button type="submit" disabled={isSaving} className="custom-button py-2 px-6 font-semibold rounded-lg shadow-sm w-full sm:w-auto">
                            {isSaving ? 'Salvando...' : 'Registrar Compromisso'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddPurchaseModal;